"use client";
import { useState } from "react";
import { cn } from "../../lib/utils";
import styles from "./pricing.module.css";

import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import HomeLayout from "../../layouts/home";
import axios from "axios";
import { API_BASE_URL } from "../../lib/constants";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Token, { JWT_COOKIE_NAME } from "../../lib/cookie";
import Cookie from "../../lib/cookie";
import { useLoggedIn } from "../../hooks/use-logged-in";
import { useTranslation } from "../../contexts/I18nProvider";

import ChooseLanguageAndCheckout from "./_components/ChooseLanguageAndCheckout";
import { useLanguageBooks } from "../Polyglot/_hooks/useLanguageBooks";
import { fetchHasPaidRequest } from "../../redux/slices/hasPaidSlice";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectLanguagesData,
  selectPurchasedLanguages,
} from "../../redux/selectors/languageSelectors";
import { useToast } from "../../hooks/use-toast";
import { Toast, ToastProvider } from "../../components/ui/toast";

export const frequencies = [
  { id: "1", value: "1", label: "Monthly", priceSuffix: "/language /month" },
  { id: "2", value: "2", label: "Annually", priceSuffix: "/language /year" },
];

export const tiers = [
  {
    name: "Free",
    id: "free",
    href: "/get-started",
    price: { 1: "$0", 2: "$0" },
    discountPrice: { 1: "", 2: "" },
    description: `Start learning African languages with our free tier.`,
    features: [
      `10 expressions per language`,
      `Access to selected African languages`,
      `Basic learning tools`,
    ],
    featured: false,
    highlighted: false,
    soldOut: false,
    cta: `Get Started`,
  },
  {
    name: "Monthly",
    id: "monthly",
    href: "/subscribe",
    price: { 1: "$1", 2: "$10" },
    discountPrice: { 1: "", 2: "" },
    description: `Premium access with full language support. Perfect for dedicated learners.`,
    features: [
      `Unlimited expressions per language`,
      `All African languages available`,
      `Enhanced learning tools`,
      `Personalized learning paths`,
    ],
    featured: true,
    highlighted: true,
    badge: "MOST POPULAR",
    soldOut: false,
    cta: `Proceed To Checkout`,
  },
  {
    name: "Annual",
    id: "annual",
    href: "/subscribe",
    price: { 1: "$1", 2: "$10" },
    discountPrice: { 1: "", 2: "" },
    description: `Best value with annual commitment. Save 17% compared to monthly billing.`,
    features: [
      `Unlimited expressions per language`,
      `All African languages available`,
      `Enhanced learning tools`,
      `Personalized learning paths`,
      `Priority email support`,
    ],
    featured: false,
    highlighted: false,
    badge: "Save 17%",
    soldOut: false,
    cta: `Proceed To Checkout`,
  },
];

const CheckIcon = ({ className }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("w-6 h-6", className)}
    >
      <path
        fillRule="evenodd"
        d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
        clipRule="evenodd"
      />
    </svg>
  );
};

export default function PricingPage() {
  const { t, getResource } = useTranslation();
  const [frequency, setFrequency] = useState(frequencies[0]);
  const dispatch = useDispatch();
  const { isLoggedIn } = useLoggedIn();
  const [selectedTier, setSelectedTier] = useState(null); // Track selected tier for language selection

  const bannerText = "";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [selectedLanguages, setSelectedLanguages] = useState([]);

  useLanguageBooks();

  // Build tiers dynamically from translations
  const dynamicTiers = [
    {
      name: t('landing.pricing.free.name'),
      id: "free",
      href: "/get-started",
      price: { 1: "$0", 2: "$0" },
      discountPrice: { 1: "", 2: "" },
      description: t('landing.pricing.free.description'),
      features: getResource('landing.pricing.free.features') || [],
      featured: false,
      highlighted: false,
      soldOut: false,
      cta: t('landing.pricing.free.cta'),
    },
    {
      name: t('landing.pricing.monthly.name'),
      id: "monthly",
      href: "/subscribe",
      price: { 1: "$1", 2: "$10" },
      discountPrice: { 1: "", 2: "" },
      description: t('landing.pricing.monthly.description'),
      features: getResource('landing.pricing.monthly.features') || [],
      featured: true,
      highlighted: true,
      badge: t('landing.pricing.badge.mostPopular'),
      soldOut: false,
      cta: t('landing.pricing.monthly.cta'),
    },
    {
      name: t('landing.pricing.annual.name'),
      id: "annual",
      href: "/subscribe",
      price: { 1: "$1", 2: "$10" },
      discountPrice: { 1: "", 2: "" },
      description: t('landing.pricing.annual.description'),
      features: getResource('landing.pricing.annual.features') || [],
      featured: false,
      highlighted: false,
      badge: t('landing.pricing.badge.save'),
      soldOut: false,
      cta: t('landing.pricing.annual.cta'),
    },
  ];

  useLanguageBooks();

  useEffect(() => {
    dispatch(fetchHasPaidRequest);
  }, [dispatch]);

  const handleCtaClick = (tier) => () => {
    let token = Cookie.get(JWT_COOKIE_NAME);
    if (!token) {
      navigate("/get-started?tab=login");
      return;
    }

    // Open the language selection flow by setting the selected tier
    setSelectedTier(tier);
    setSelectedLanguages([]); // Reset languages when changing tier
    
    // Set the frequency based on the tier clicked
    if (tier.id === "monthly") {
      setFrequency(frequencies[0]); // Monthly
    } else if (tier.id === "annual") {
      setFrequency(frequencies[1]); // Annually
    }
  };

  const handleLanguageCheckout = async () => {
    if (!selectedTier || !selectedLanguages || selectedLanguages.length === 0) {
      setError("Please select at least one language to continue.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Calculate price based on tier
      const pricePerLanguage = selectedTier.id === "annual" ? 10 : 1;
      const amount = pricePerLanguage * selectedLanguages.length * 100;
      const currency = "usd";
      
      // Map tier names to schema values
      const tierMap = {
        "free": "Standard",
        "monthly": "Standard",
        "annual": "Premium"
      };
      const apiTier = tierMap[selectedTier.id] || "Standard";
      
      // Ensure frequency is "Monthly" or "Annually"
      const apiFrequency = frequency.label === "Monthly" ? "Monthly" : "Annually";

      console.log("Payment Intent Request:", {
        amount,
        currency,
        frequency: apiFrequency,
        tier: apiTier,
        selectedLanguages: selectedLanguages.map((item) => item.id),
      });

      const response = await axios.post(
        API_BASE_URL + "/api/payment-intent/create",
        {
          amount,
          currency,
          frequency: apiFrequency,
          tier: apiTier,
          selectedLanguages: selectedLanguages
            .map((item) => item.id)
            .join(","),
        },
        {
          headers: {
            Authorization: `Bearer ${Cookie.get(JWT_COOKIE_NAME)}`,
          },
        }
      );

      const { clientSecret, recentPaymentIntent } = response.data;
      const params = new URLSearchParams({
        client_secret: clientSecret,
        has_paid: recentPaymentIntent?.has_paid || "false",
        paid_at: recentPaymentIntent?.paid_at || "",
        subscription_expires_at:
          recentPaymentIntent?.subscription_expires_at || "",
        tier: recentPaymentIntent?.tier || "",
        frequency: recentPaymentIntent?.frequency || "",
      });

      navigate(`/payment-methods/add?${params.toString()}`);
    } catch (err) {
      console.error("Payment Intent Error:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "An error occurred while creating the payment intent. Please check the console.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Show language selection flow when a paid tier is selected */}
      {selectedTier && (
        <ChooseLanguageAndCheckout
          tier={selectedTier}
          frequency={frequency}
          setFrequency={setFrequency}
          frequencies={frequencies}
          selectedLanguages={selectedLanguages}
          setSelectedLanguages={setSelectedLanguages}
          handleCtaClick={handleLanguageCheckout}
          loading={loading}
          onClose={() => setSelectedTier(null)}
          isFrequencyLocked={true}
          pricePerLanguage={selectedTier.id === "annual" ? 10 : 1}
        />
      )}
      
      {/* Show pricing tiers when no tier is selected */}
      {!selectedTier && (
        <HomeLayout>
          <ToastProvider>
            <Toast />
          </ToastProvider>
          <div
            className={cn("flex flex-col w-full items-center bg-gradient-to-br from-white via-gray-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-950", styles.fancyOverlay)}
          >
        <div className="w-full flex flex-col items-center mb-24">
          <div className="mx-auto max-w-7xl px-6 xl:px-8">
            <div className="mx-auto max-w-3xl sm:text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-black dark:text-white mb-6">
                {t('pages.pricingPage.title')}
              </h2>
              <p className="text-lg leading-8 text-gray-700 dark:text-gray-300">
                {t('pages.pricingPage.subtitle')}
              </p>
            </div>
            {bannerText ? (
              <div className="flex justify-center my-4">
                <p className="px-4 py-3 text-xs bg-accent text-black dark:bg-indigo-300/30 dark:text-white/80 rounded-xl">
                  {bannerText}
                </p>
              </div>
            ) : null}
            {error ? (
              <Alert className="mt-3">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            
            {/* Pricing Tiers Grid */}
            <div className="mx-auto mt-20 max-w-2xl space-y-8 overflow-visible lg:mx-0 lg:max-w-none lg:grid lg:grid-cols-3 lg:gap-8">
              {dynamicTiers.map((tier) => (
                <div
                  key={tier.id}
                  className={cn(
                    "flex flex-col rounded-2xl shadow-lg transition-all duration-300 hover:shadow-2xl ring-1 relative overflow-visible",
                    tier.highlighted
                      ? "lg:z-10 lg:ring-2 lg:ring-indigo-600 lg:scale-105 bg-white dark:bg-gray-800"
                      : "ring-gray-200 dark:ring-gray-700 bg-white dark:bg-gray-900",
                    tier.featured
                      ? "bg-gradient-to-br from-indigo-50/50 to-white dark:from-indigo-900/20 dark:to-gray-800"
                      : ""
                  )}
                >
                  <div className="p-8 sm:p-10">
                    {/* Badge for Featured/Popular tiers */}
                    {tier.badge && (
                      <div className="mb-4">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-4 py-1 text-sm font-semibold",
                            tier.featured
                              ? "bg-indigo-600 text-white"
                              : "bg-green-600 text-white"
                          )}
                        >
                          {tier.badge}
                        </span>
                      </div>
                    )}

                    <h3 className="text-2xl font-bold tracking-tight text-black dark:text-white">
                      {tier.name}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                      {tier.description}
                    </p>

                    {/* Price Display */}
                    <p className="mt-6 flex items-baseline gap-x-2">
                      <span className="text-5xl font-bold tracking-tight text-black dark:text-white whitespace-nowrap">
                        {tier.id === "monthly" ? "$1" : tier.id === "annual" ? "$10" : "$0"}
                      </span>
                      {tier.name !== "Free" && (
                        <span className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                          {tier.id === "annual" ? "per language /year" : "per language /month"}
                        </span>
                      )}
                    </p>

                    {/* CTA Button */}
                    {tier.name === "Free" ? (
                      <button
                        onClick={() => {
                          if (isLoggedIn) {
                            navigate('/polyglot-legacy');
                          } else {
                            navigate('/get-started?tab=login');
                          }
                        }}
                        className="mt-6 block w-full rounded-lg bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        {t('pages.pricingPage.buttonSelectPlan')}
                      </button>
                    ) : (
                      <button
                        onClick={handleCtaClick(tier)}
                        className={cn(
                          "mt-6 block w-full rounded-lg px-3 py-2 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                          tier.featured
                            ? "bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600"
                            : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50 focus-visible:outline-indigo-600"
                        )}
                      >
                        Proceed To Checkout
                      </button>
                    )}

                    {/* Features List */}
                    <div className="mt-8">
                      <h4 className="text-sm font-semibold leading-6 text-black dark:text-white">
                        {t('pages.pricingPage.features')}
                      </h4>
                      <ul className="mt-4 space-y-3">
                        {Array.isArray(tier.features) ? (
                          tier.features.map((feature) => (
                            <li
                              key={feature}
                              className="flex items-center gap-x-3 text-sm leading-6 text-gray-700 dark:text-gray-300"
                            >
                              <CheckIcon
                                className="h-6 w-6 flex-none text-indigo-600 dark:text-indigo-400"
                                aria-hidden="true"
                              />
                              {feature}
                            </li>
                          ))
                        ) : null}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
        </HomeLayout>
      )}
    </>
  );
}
